<template>
  <AdminLayout>
    <v-row>
      <v-col cols="12">
        <v-alert
          class="custom-alert"
          v-if="!bookableCountCheck"
          type="info"
          elevation="2"
        >
          Sie haben die maximale Anzahl an öffentlichen Buchungsobjekten
          erreicht. Erweitern Sie Ihr Kontingent, oder löschen Sie nicht mehr
          benötigte Buchungsobjekte.
        </v-alert>
      </v-col>
    </v-row>
    <BookableSearchAndFilter
      type="Veranstaltungsorte"
      @update:search="searchQuery = $event"
      @update:filters="filters = $event"
    />
    <v-row class="align-stretch pb-15" :gutter="16">
      <v-col
        cols="12"
        sm="6"
        md="4"
        lg="3"
        v-for="location in filteredLocations"
        :key="location.id"
        class="d-flex"
      >
        <v-skeleton-loader type="card" class="flex" :loading="loading">
          <bookable-card
            :item="location"
            edit-route="location-edit"
            :from-route="$router.currentRoute.value.name"
            @delete="removeBookable(location.id)"
            @duplicate="duplicateBookable(location.id)"
          />
        </v-skeleton-loader>
      </v-col>
    </v-row>
    <v-fab-transition>
      <v-btn
        color="primary"
        fixed
        size="large"
        rounded
        class="position-fixed bottom-0 right-0 mb-4 mr-5"
        :to="{
          name: 'location-edit',
          query: { fromRoute: $router.currentRoute.value.name },
        }"
        :disabled="createDisabled"
      >
        <v-icon>mdi-plus</v-icon>
        Veranstaltungsort Erstellen
      </v-btn>
    </v-fab-transition>
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/layouts/Admin.vue";
import { mapGetters, mapActions } from "vuex";
import BookableCard from "@/components/BookableCard.vue";
import BookableSearchAndFilter from "@/components/Filters/BookableSearchAndFilter.vue";
import ApiBookablesService from "@/services/api/ApiBookablesService";
import ToastService from "@/services/ToastService";
import BookablePermissionService from "@/services/permissions/BookablePermissionService";
export default {
  components: {
    AdminLayout,
    BookableCard,
    BookableSearchAndFilter,
  },
  data() {
    return {
      api: {
        locations: [],
        tags: [],
      },
      filters: {},
      searchQuery: "",
      bookableCountCheck: true,
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading/isLoading",
      tenantId: "tenants/currentTenantId",
    }),
    createDisabled() {
      return !this.BookablePermissionService.allowCreate();
    },
    BookablePermissionService() {
      return BookablePermissionService;
    },
    filteredLocations() {
      const locations = this.api.locations || [];
      const f = this.filters;

      const hasFilter =
        this.searchQuery ||
        (f.selectedTags && f.selectedTags.length > 0) ||
        f.isBookable !== null ||
        f.isPublic !== null ||
        f.allowRecurringBooking !== null ||
        f.autoCommitBooking !== null;

      if (!hasFilter) return locations;

      return locations.filter((location) => {
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          const matchesSearch =
            (location.title && location.title.toLowerCase().includes(query)) ||
            (location.description &&
              location.description.toLowerCase().includes(query)) ||
            (location.location &&
              location.location.toLowerCase().includes(query));
          if (!matchesSearch) return false;
        }
        if (f.selectedTags && f.selectedTags.length > 0) {
          const hasAllTags = f.selectedTags.every((tag) =>
            location.tags?.includes(tag)
          );
          if (!hasAllTags) return false;
        }
        if (f.isBookable !== null && location.isBookable !== f.isBookable)
          return false;
        if (f.isPublic !== null && location.isPublic !== f.isPublic)
          return false;
        if (
          f.allowRecurringBooking !== null &&
          location.allowRecurringBooking !== f.allowRecurringBooking
        )
          return false;
        if (
          f.autoCommitBooking !== null &&
          location.autoCommitBooking !== f.autoCommitBooking
        )
          return false;

        return true;
      });
    },
  },
  watch: {
    tenantId() {
      this.fetchLocations();
    },
  },
  methods: {
    ...mapActions({
      startLoading: "loading/start",
      stopLoading: "loading/stop",
      addToast: "toasts/add",
    }),
    async removeBookable(bookableId) {
      await this.startLoading("fetch-delete-bookable");
      ApiBookablesService.deleteBookable(bookableId)
        .then(() => {
          this.fetchLocations();
        })
        .finally(() => {
          this.addToast(
            ToastService.createToast("bookable.delete.success", "success")
          );
          this.stopLoading("fetch-delete-bookable");
        })
        .catch((error) => {
          this.addToast(
            ToastService.createToast(
              "bookable.duplicate.errors.something-wrong",
              "error"
            )
          );
          console.log(error);
        });
      await this.getBookableCount();
    },
    async duplicateBookable(bookableId) {
      await this.startLoading("fetch-duplicate-bookable");

      ApiBookablesService.duplicateBookable(bookableId)
        .then(() => {
          this.fetchLocations();
        })
        .finally(() => {
          this.addToast(
            ToastService.createToast("bookable.duplicate.success", "success")
          );
          this.stopLoading("fetch-duplicate-bookable");
        })
        .catch((error) => {
          this.addToast(
            ToastService.createToast("errors.something-wrong", "error")
          );
          console.log(error);
        });
      await this.getBookableCount();
    },
    remove(item) {
      this.filters.splice(this.filters.indexOf(item), 1);
    },
    fetchLocations() {
      this.startLoading("fetch-locations");
      ApiBookablesService.getBookables()
        .then((response) => {
          this.api.locations = response.data.filter(
            (resource) => resource.type === "event-location"
          );
        })
        .finally(() => {
          this.stopLoading("fetch-locations");
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async getBookableCount() {
      this.bookableCountCheck =
        await ApiBookablesService.publicBookableCountCheck();
    },
  },
  async mounted() {
    await this.getBookableCount();
  },
  created() {
    this.fetchLocations();
  },
};
</script>

<style scoped>
.filter-field {
  border-radius: 15px;
}
.custom-alert {
  border-radius: 15px !important;
}
</style>
